import { useCallback, useEffect, useState } from 'react';

import type { HarnessCliMachineView, HarnessCliOverview, HarnessCliRowView } from '@nightshift/ui-types';

import { Kicker, RefreshError, Since, Spinner } from '../components/primitives.tsx';
import { api, type SessionUser } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import { canWriteSettings } from '../lib/privilege.ts';

type Policy = HarnessCliOverview['policy'];

const POLICY_LABELS: Record<Policy, string> = {
  off: 'Off',
  notify: 'Notify',
  'auto-when-idle': 'Auto when idle',
};
const POLICIES = Object.keys(POLICY_LABELS) as Policy[];

const OUTCOME_LABELS: Record<NonNullable<HarnessCliRowView['lastUpdate']>['outcome'], string> = {
  updated: 'Updated',
  current: 'No newer version',
  failed: 'Update failed',
  locked: 'CLI in use',
  'timed-out': 'Update timed out',
  'smoke-failed': 'Smoke check failed',
  'needs-manual': 'Needs manual update',
};

function statusOf(row: HarnessCliRowView): { tone: 'ok' | 'attention' | 'quiet'; text: string; title?: string } {
  if (row.state === 'excluded') return { tone: 'attention', text: 'Excluded', ...(row.stateDetail ? { title: row.stateDetail } : {}) };
  if (row.state === 'updating') return { tone: 'attention', text: 'Updating' };
  if (row.state === 'queued') return { tone: 'quiet', text: 'Queued', ...(row.stateDetail ? { title: row.stateDetail } : {}) };
  if (row.requestPending) return { tone: 'quiet', text: 'Update requested' };
  if (row.updateAvailable) {
    return row.updatable
      ? { tone: 'attention', text: 'Update available' }
      : { tone: 'attention', text: 'Needs manual update', ...(row.manualReason ? { title: row.manualReason } : {}) };
  }
  if (row.installed === null) return { tone: 'attention', text: row.manualReason?.includes('not installed') ? 'Not installed' : 'Not answering', ...(row.stateDetail ? { title: row.stateDetail } : {}) };
  if (row.latest === null) return { tone: 'quiet', text: 'Latest unknown' };
  return { tone: 'ok', text: 'Current' };
}

/**
 * Managed clients across the fleet: what each machine runs against the latest, how each was
 * installed, and whether the runner can update it. Admins set the policy and press Update now;
 * everyone else reads.
 */
export function HarnessCliPanel({ user }: { user: SessionUser | null }) {
  const live = useLive();
  const store = useStore();
  const canWrite = canWriteSettings(user);
  const [overview, setOverview] = useState<HarnessCliOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // A policy choice shows as chosen while it is saved and the panel reloads, rather than
  // snapping back to the stored value for a beat and then jumping forward.
  const [pending, setPending] = useState<Record<string, Policy | ''>>({});

  const poke = `${live.snapshot?.revision ?? 0}:${live.lastContactAt}`;

  const load = useCallback(async () => {
    try {
      setOverview(await api.harnessCli());
      setLoadedAt(Date.now());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, poke]);

  const act = async (key: string, run: () => Promise<{ message?: string }>) => {
    setBusy(key);
    try {
      const result = await run();
      if (result.message) store.toast('good', result.message);
      await load();
    } catch (caught) {
      store.toast('bad', caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
      setPending((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  if (!overview) {
    return (
      <section className="sheet-panel harness-cli" aria-busy="true">
        <header className="panel-head hair-b">
          <Kicker>Managed clients</Kicker>
        </header>
        <div className="sheet-panel-body">{error ? <p className="sheet-note">{error}</p> : <Spinner />}</div>
      </section>
    );
  }

  const rows = overview.machines.reduce((count, machine) => count + machine.harnesses.length, 0);

  return (
    <section className="sheet-panel harness-cli" data-harness-cli-policy={overview.policy} aria-busy={busy !== null}>
      <header className="panel-head hair-b harness-cli-head">
        <Kicker count={rows}>Managed clients</Kicker>
        <label className="harness-cli-policy">
          <span className="dim">Updates</span>
          <select
            className="input"
            aria-label="CLI update policy for every machine"
            value={pending.policy || overview.policy}
            disabled={!canWrite || busy !== null}
            onChange={(event) => {
              const policy = event.target.value as Policy;
              setPending((current) => ({ ...current, policy }));
              void act('policy', () => api.setHarnessUpdatePolicy(policy));
            }}
          >
            {POLICIES.map((policy) => (
              <option key={policy} value={policy}>
                {POLICY_LABELS[policy]}
              </option>
            ))}
          </select>
        </label>
        <span className="harness-cli-checked dim">
          {overview.checkedAt === null ? 'Latest versions not looked up yet' : <>Latest looked up <Since at={overview.checkedAt} now={live.tick} /></>}
        </span>
        {canWrite ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy !== null}
            onClick={() => void act('check', async () => {
              const result = await api.checkHarnessLatest();
              setOverview(result.overview);
              return { message: 'Latest versions looked up' };
            })}
          >
            Check now
          </button>
        ) : null}
      </header>
      {error ? (
        <RefreshError what="The CLI list" error={error} since={loadedAt} now={live.tick} onRetry={() => void load()} />
      ) : null}
      <div className="sheet-panel-body harness-cli-latest">
        {overview.latest
          .filter((entry) => entry.version !== null || entry.error !== null)
          .map((entry) => (
            <span key={entry.source} className="harness-cli-latest-item" title={entry.error ?? entry.from ?? undefined}>
              <span className="dim">{entry.label}</span>{' '}
              {entry.version ? <span className="mono">{entry.version}</span> : <span className="machines-muted">unknown</span>}
              {entry.error ? <span className="machines-health-attention"> · lookup failed</span> : null}
            </span>
          ))}
        {canWrite ? <label className="harness-cli-force"><input type="checkbox" checked={force} disabled={busy !== null} onChange={event => setForce(event.target.checked)} /> Force update while running (may interrupt active threads or restart the app)</label> : null}
        <p className="sheet-note">
          Notify shows what is behind. Auto when idle updates a client only when nothing on that machine is using it, and
          never with elevation. Update now does the same once, whatever the policy, unless force is checked.
        </p>
      </div>
      {rows === 0 ? (
        <p className="sheet-note harness-cli-empty">No runner has reported its CLIs yet. A runner reports them on its first heartbeat after it runs this build.</p>
      ) : (
        <div className="harness-cli-scroll">
        <table className="table harness-cli-table">
          <thead>
            <tr>
              <th>Machine and policy</th>
              <th>Client</th>
              <th>Installed</th>
              <th>Latest</th>
              <th>Method</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {overview.machines
              .filter((machine) => machine.harnesses.length > 0)
              .flatMap((machine) =>
                machine.harnesses.map((row, index) => (
                  <HarnessRow
                    key={`${machine.machineId}:${row.harness}`}
                    machine={machine}
                    globalPolicy={overview.policy}
                    pendingPolicy={pending[`policy:${machine.machineId}`]}
                    row={row}
                    first={index === 0}
                    span={machine.harnesses.length}
                    canWrite={canWrite}
                    busy={busy}
                    now={live.tick}
                    onPolicy={(policy) => {
                      const key = `policy:${machine.machineId}`;
                      setPending((current) => ({ ...current, [key]: policy ?? '' }));
                      void act(key, () => api.setMachineHarnessUpdatePolicy(machine.machineId, policy));
                    }}
                    onUpdate={() => void act(`update:${machine.machineId}:${row.harness}`, () => api.updateHarnessCli(machine.machineId, row.harness, force))}
                  />
                )),
              )}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

function HarnessRow({
  machine,
  globalPolicy,
  pendingPolicy,
  row,
  first,
  span,
  canWrite,
  busy,
  now,
  onPolicy,
  onUpdate,
}: {
  machine: HarnessCliMachineView;
  globalPolicy: Policy;
  pendingPolicy: Policy | '' | undefined;
  row: HarnessCliRowView;
  first: boolean;
  span: number;
  canWrite: boolean;
  busy: string | null;
  now: number;
  onPolicy: (policy: Policy | null) => void;
  onUpdate: () => void;
}) {
  const status = statusOf(row);
  const last = row.lastUpdate;
  const updateBlocked = !row.updatable || row.state === 'updating' || row.requestPending;
  return (
    <tr data-harness={row.harness} data-machine={machine.name} data-attention={status.tone === 'attention' ? 'true' : undefined}>
      {first ? (
        <td className="harness-cli-machine" rowSpan={span}>
          <div className="mono machines-name">
            {machine.name}
            {machine.live ? null : <span className="machines-muted"> · offline</span>}
          </div>
          <select
              className="input harness-cli-machine-policy"
              aria-label={`CLI update policy for ${machine.name}`}
              value={pendingPolicy ?? machine.policyOverride ?? ''}
              disabled={!canWrite || busy !== null}
              onChange={(event) => onPolicy(event.target.value === '' ? null : (event.target.value as Policy))}
            >
              <option value="">Global ({POLICY_LABELS[globalPolicy]})</option>
              {POLICIES.map((policy) => (
                <option key={policy} value={policy}>
                  {POLICY_LABELS[policy]}
                </option>
              ))}
          </select>
        </td>
      ) : null}
      <td>{row.label}</td>
      <td className="mono">{row.installed ?? <span className="machines-muted">—</span>}</td>
      <td className="mono">{row.latest ?? <span className="machines-muted">—</span>}</td>
      <td>
        <span title={row.updateCommand ?? row.manualReason ?? undefined}>{row.methodLabel}</span>
        {row.updatable ? null : (
          <div className="harness-cli-note machines-muted" title={row.manualReason ?? undefined}>
            {row.manualReason ?? 'needs manual update'}
          </div>
        )}
      </td>
      <td>
        <span className={status.tone === 'attention' ? 'machines-health-attention' : status.tone === 'ok' ? 'dim' : 'machines-muted'} title={status.title}>
          {status.text}
        </span>
        {row.state === 'queued' && row.stateDetail ? <div className="harness-cli-note machines-muted">{row.stateDetail}</div> : null}
        {row.state === 'excluded' && row.stateDetail ? <div className="harness-cli-note machines-health-attention">{row.stateDetail}</div> : null}
        {last ? (
          <details className="harness-cli-last">
            <summary>
              {OUTCOME_LABELS[last.outcome]} <Since at={last.at} now={now} short />
            </summary>
            <div className="harness-cli-note">{last.detail}</div>
            {last.outputTail ? <pre className="harness-cli-output">{last.outputTail}</pre> : null}
          </details>
        ) : null}
      </td>
      <td className="right">
        {canWrite ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={updateBlocked || busy !== null}
            title={row.updatable ? (row.updateCommand ?? undefined) : (row.manualReason ?? undefined)}
            onClick={onUpdate}
          >
            Update now
          </button>
        ) : null}
      </td>
    </tr>
  );
}
